import React from 'react'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { useRequirementByIdQuery } from 'queries/requirements'

type RequirementDetailContextState = {
  state: NonNullable<ReturnType<typeof useRequirementByIdQuery>['data']> & {
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
  }
  actions: {
    refetch: ReturnType<typeof useRequirementByIdQuery>['refetch']
  }
}

const [RequirementDetailContext, useRequirementDetailContextValue] =
  createContext<RequirementDetailContextState>({
    name: 'RequirementDetailContext',
  })

export const RequirementDetailProvider = ({
  children,
  requirementId,
}: {
  children: React.ReactNode
  requirementId: number
}) => {
  const {
    data,
    isPending: isRequirementDetailLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useRequirementByIdQuery(requirementId)

  if (isRequirementDetailLoading) {
    return <Loading />
  }

  if (isError && !data) {
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: RequirementDetailContextState = {
    state: {
      ...data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
    },
    actions: {
      refetch,
    },
  }

  return (
    <RequirementDetailContext.Provider value={value}>
      {children}
    </RequirementDetailContext.Provider>
  )
}

export { useRequirementDetailContextValue }
